import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, FormHelperText } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { newsApi } from '../../api/news';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import PageLayout from '../../components/layout/PageLayout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller } from 'react-hook-form';
import { formatDateForInput } from '../../utils/formatter';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import ProgressBar from '../../components/common/ProgressBar';
import CustomTextField from '../../components/common/CustomTextField';
import RepresentativeImageSelector, {
  getRepresentativeImageUrl,
} from '../../components/common/RepresentativeImageSelector';
import theme from '../../theme';

const schema = yup.object().shape({
  title: yup.string().required('제목을 입력하세요.'),
  linkUrl: yup
    .string()
    .required('원본기사 URL을 입력하세요.')
    .test('is-https', '올바른 URL 형식을 입력하세요.', (value) => {
      if (!value) return true; // required에서 처리
      return value.startsWith('https://');
    }),
  sourceUrl: yup.string().required('출처 표시를 입력하세요.'),
  releaseDate: yup.string().required('날짜를 입력하세요.'),
  content: yup.string().required('설명을 입력하세요.'),
});

const NewsForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const returnPage = searchParams.get('returnPage');

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      linkUrl: '',
      sourceUrl: '',
      releaseDate: new Date().toISOString().split('T')[0],
      content: '',
    },
  });

  const navigate = useNavigate();
  const content = watch('content');
  const [representativeImageUrl, setRepresentativeImageUrl] = useState('');
  const [initialImageUrl, setInitialImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (isEditMode) {
      const fetchNews = async () => {
        try {
          const response = await newsApi.getNewsById(id);
          const news = response.data;

          setValue('title', news.title);
          setValue('linkUrl', news.linkUrl);
          setValue('sourceUrl', news.sourceUrl);
          setValue('releaseDate', formatDateForInput(news.releaseDate));
          setValue('content', news.content);

          if (news.imageUrl) {
            setRepresentativeImageUrl(news.imageUrl);
            setInitialImageUrl(news.imageUrl);
          }
        } catch (error) {
          console.error('Error fetching news:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchNews();
    }
  }, [id, isEditMode, setValue]);

  const onSubmit = async (formData) => {
    try {
      const imageUrl = getRepresentativeImageUrl(
        formData.content,
        representativeImageUrl,
        isEditMode ? initialImageUrl : '',
      );

      if (isEditMode) {
        await newsApi.updateNews(id, { ...formData, imageUrl });
        navigate(`/news/${id}`);
      } else {
        // 생성 모드
        const response = await newsApi.createNews({ ...formData, imageUrl });
        navigate(`/news/${response.data.slug || response.data.id}`);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      if (!isEditMode) {
        reset();
      }
    }
  };

  const handleCancel = () => {
    if (from === 'detail') {
      navigate(`/news/${id}`);
    } else if (from === 'list' && returnPage) {
      navigate(`/news?page=${returnPage}`);
    } else {
      navigate('/news');
    }
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box sx={{ maxWidth: '780px', mx: 'auto' }}>
          <Stack spacing={'30px'}>
            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  제목
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  원본기사 URL
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('linkUrl')}
                  error={!!errors.linkUrl}
                  helperText={errors.linkUrl?.message}
                  placeholder="원본기사 URL을 입력하세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  출처 표시
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('sourceUrl')}
                  error={!!errors.sourceUrl}
                  helperText={errors.sourceUrl?.message}
                  placeholder="출처 표시를 입력하세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  날짜
                </Typography>
                <CustomTextField
                  fullWidth
                  type="date"
                  {...register('releaseDate')}
                  error={!!errors.releaseDate}
                  helperText={errors.releaseDate?.message}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  설명
                </Typography>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <ReactQuillEditor value={field.value} onChange={field.onChange} />
                      <RepresentativeImageSelector
                        content={content}
                        value={representativeImageUrl}
                        onChange={setRepresentativeImageUrl}
                      />
                      {errors.content && (
                        <FormHelperText
                          error
                          sx={{
                            textAlign: 'left',
                            margin: '4px 0 0 0',
                            fontSize: '12px',
                            color: theme.palette.red,
                          }}
                        >
                          {errors.content.message}
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                />
              </Stack>
            </Stack>

            {/* 버튼 */}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={() => setIsCancelDialogOpen(true)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  등록
                </BasicButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </ProgressBar>
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancel}
      />
    </PageLayout>
  );
};

export default NewsForm;
